import {Elastic, Sine, TimelineMax} from "gsap";
import {AudioChannel} from "../Core/AudioChannel";
import {BaseButton, ButtonEventListener, HoverableButton, TextureButton} from "../Core/Button";
import {IPoint} from "../Core/Geom";
import {Callback, Mix} from "../Core/Reflection";
import {voOptionsButtons} from "../Data/VoiceOver";
import {Focusable} from "./Focusable";
import {VoiceOverManager} from "../Managers/VoiceOverManager";
import {ISignal, Signal} from "../Core/Signal";
import * as PIXI from "pixi.js";

export class Button extends Mix(TextureButton, HoverableButton(), Focusable) {

    public clickSfx?: string;
    public hoverVO?: string | string[];
    public hoverVOOnce?: boolean;

    constructor(texture?: PIXI.Texture | string, onClick?: ButtonEventListener<Button>, anchor?: IPoint) {
        super(texture, onClick, anchor);

        this.clickSfx = "ui_click";

        this.onOver.add(() => {
            if (this.hoverVO && !PIXI.utils.isMobile.any) {
                VoiceOverManager.instance.playVO(this.hoverVO, {
                    ...voOptionsButtons,
                    callback: this.hoverVOOnce ? b => {
                        if (b) this.hoverVO = undefined;
                    } : undefined
                });
            }
        });
        this.onDown.add(() => {
            if (this.clickSfx) {
                AudioChannel.SFX.play(this.clickSfx);
            }
        });
    }
}

export const PulsatingButton = {
    onPulse: undefined as any as ISignal<Callback>,
    // Do not use this argument here because it's not compiled properly with arguments having default values
    pulse(scale = 1.15, interval = 1.5, startImmediately = false) {
        if (!this.onPulse) {
            this.onPulse = Signal<Callback>();
        }
        const b = this as any as BaseButton;
        (this as any).stopPulse();
        const initScale = b.scale.x;
        const loop = new TimelineMax({repeat: -1})
            .call(this.onPulse, undefined, interval)
            .to(this, {
                duration: 0.15,
                scaleX: initScale * scale,
                scaleY: initScale * scale,
                ease: Sine.easeInOut,
            })
            .to(this, {
                duration: 1,
                scaleX: initScale,
                scaleY: initScale,
                ease: Elastic.easeOut,
            })
            .call(() => {
                loop.seek(0);
                if (b.isOver) {
                    loop.pause();
                }
                if ((b as any)._pulsatingButtonStopPulse) {
                    PulsatingButton.stopPulse.call(b, true);
                }
            });
        if (startImmediately) {
            loop.seek(interval);
        }
        const onOut = () => {
            loop.resume();
        };
        b.onOut.add(onOut);
        const destroy = b.destroy;
        b.destroy = () => {
            loop.kill();
            destroy.call(b);
        };
        (this as any)._pulsatingButtonOriginalDestroy = destroy;
        (this as any)._pulsatingButtonLoop = loop;
        (this as any)._pulsatingButtonOnOut = onOut;
        (this as any)._pulsatingButtonInitScale = initScale;
        (this as any)._pulsatingButtonStopPulse = false;
    },
    stopPulse(immediately = true) {
        if ((this as any)._pulsatingButtonLoop) {
            if (immediately) {
                const b = this as any as BaseButton;
                b.destroy = (this as any)._pulsatingButtonOriginalDestroy;
                b.onOut.remove((this as any)._pulsatingButtonOnOut);
                (this as any)._pulsatingButtonLoop.kill();
                (this as any)._pulsatingButtonLoop = undefined;
                b.scale.set((this as any)._pulsatingButtonInitScale);
            } else {
                (this as any)._pulsatingButtonStopPulse = true;
            }
        }
    },
    pausePulse() {
        if ((this as any)._pulsatingButtonLoop) {
            (this as any)._pulsatingButtonLoop.pause();
        }
    },
    resumePulse() {
        if ((this as any)._pulsatingButtonLoop) {
            (this as any)._pulsatingButtonLoop.resume();
        }
    }
};

export class ButtonPulse extends Mix(Button, PulsatingButton) {
}

export class EdgeButton extends Button {

    private offset = 0;

    onLayout() {
        this.offset = this.x;
    }

    setPosition(size: IPoint) {
        this.x = this.offset + Math.sign(this.offset) * (size.x - 1024);
    }
}

export class HomeButton extends EdgeButton {

    constructor() {
        super();

        this.clickSfx = "ui_select";
        this.hoverVO = "patw_ui15";
    }
}

export class CheckmarkButton extends ButtonPulse {

    constructor() {
        super();

        this.hoverVO = "LetsGoLuna_014";
    }
}
